import { useEffect, useRef, useState } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { PanelLeft } from "lucide-react";
import { Header } from "@/components/shell/Header";
import { IconTooltip } from "@/components/shell/IconTooltip";
import { NavigationRecentsTracker } from "@/components/shell/NavigationRecentsTracker";
import { Sidebar } from "@/components/shell/Sidebar";
import { SidebarResizeHandle } from "@/components/shell/SidebarResizeHandle";
import DemoBanner from '@/components/shell/DemoBanner';
import { useAuth } from '@/state/AuthContext';
import { useWorkspaceStore } from '@/state/workspace.store';
import { useWorkspaceValidation } from '@/hooks/useWorkspaceValidation';
import { useOrgHomeState } from "@/features/organizations/useOrgHomeState";
import { platformRoleFromUser } from "@/utils/roles";
import { shouldRunAdminFirstTimeOnboarding } from "@/routing/adminOnboardingPolicy";
import {
  SIDEBAR_DEFAULT_PX,
  SIDEBAR_WIDTH_STORAGE_KEY,
  useResizableSidebar,
} from "@/hooks/useResizableSidebar";
import { useUIStore } from "@/stores/uiStore";

/** Tailwind `md` — below this the shell uses an overlay drawer, not an in-flow column. */
const MOBILE_SIDEBAR_MQ = "(max-width: 767px)";

/**
 * DashboardLayout — always renders the app shell (Sidebar + Header).
 *
 * LOCKED SHELL CONTRACT (gating: DashboardLayout.sidebarResize + SidebarResizeHandle):
 *   - Left sidebar width is user-resizable via SidebarResizeHandle (drag, double-click reset).
 *   - Hover tooltip: Close ⌘\, Resize Drag, Reset Double-click (ClickUp parity).
 *   - Width persisted in localStorage (`zephix-sidebar-width-px`). Do not revert to fixed w-72.
 *   - Desktop collapse: Sidebar brand header `sidebar-close-btn` → setSidebarOpen(false);
 *     reopen via `sidebar-reopen-btn` (z-[60], above Header z-50 — do not regress #865).
 *     Hover tooltips via shared IconTooltip (SIDEBAR-BUTTON-TOOLTIPS-1): Close (+ ⌘\ while
 *     SidebarResizeHandle is mounted); Open without shortcut (⌘\ unmounts when closed).
 *   - Below Tailwind `md` (≤767px): sidebar is a fixed overlay drawer + backdrop; Header
 *     exposes `sidebar-mobile-toggle`. Resize handle is desktop-only (`hidden md:contents`).
 *     Do not leave an in-flow flex column eating the mobile viewport.
 *   - Returning from ≤767px to ≥768px must reopen sidebar (do not regress #864). *
 * Workspace gating is handled at the route level by <RequireWorkspace />.
 *
 * GATE 1 — Redirect to `/onboarding` **only** when `shouldRunAdminFirstTimeOnboarding` is true:
 * Admin + (`not_started` | `in_progress`). Never for completed, dismissed, Member, or Viewer.
 * Waits until org onboarding status has loaded (`!orgHomeLoading`) so we do not redirect on `undefined`.
 *
 * GATE 2 — Workspace validation runs whenever a persisted workspace ID exists,
 * including during the onboarding gate. Disabling it there left stale IDs uncleared
 * exactly when a mismatch 403 could force a false onboarding redirect.
 */
export default function DashboardLayout() {
  const { user } = useAuth();
  const { activeWorkspaceId } = useWorkspaceStore();
  const { isLoading: orgHomeLoading, onboardingStatus } = useOrgHomeState();
  const sidebarOpen = useUIStore((s) => s.sidebarOpen);
  const setSidebarOpen = useUIStore((s) => s.setSidebarOpen);
  const { listPx: sidebarPx, setListPx: setSidebarPx, splitRef, onResizerPointerDown, isDragging } =
    useResizableSidebar();
  const reopenBtnRef = useRef<HTMLButtonElement>(null);
  const [reopenHover, setReopenHover] = useState(false);

  /**
   * Mobile sync: close at ≤767px so the in-flow column does not eat the viewport.
   * On return to ≥768px, reopen — otherwise `sidebarOpen:false` latches into
   * zephix-ui-storage and survives reload viewport-independently
   * (SIDEBAR-OPEN-LATCHES-FALSE-1). Do not force-open on initial desktop mount
   * (preserves an intentional desktop close across remounts).
   */
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }
    const mq = window.matchMedia(MOBILE_SIDEBAR_MQ);
    const syncViewportSidebar = (fromChange: boolean) => {
      if (mq.matches) {
        setSidebarOpen(false);
      } else if (fromChange) {
        setSidebarOpen(true);
      }
    };
    syncViewportSidebar(false);
    const onChange = () => syncViewportSidebar(true);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [setSidebarOpen]);

  const resetSidebarWidth = () => {
    setSidebarPx(SIDEBAR_DEFAULT_PX);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, String(SIDEBAR_DEFAULT_PX));
    }
  };

  /**
   * GATE 1: Brand-new Admin with incomplete org onboarding → full-page flow.
   * Requires a confirmed status value — never redirects on undefined (failed/unknown fetch).
   */
  const platformRole = platformRoleFromUser(user);
  const needsAdminOnboarding =
    Boolean(user) &&
    !orgHomeLoading &&
    shouldRunAdminFirstTimeOnboarding({ platformRole, onboardingStatus });

  useWorkspaceValidation({
    enabled: Boolean(activeWorkspaceId),
  });
  if (needsAdminOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  return (
    <div
      ref={splitRef}
      data-testid="app-shell"
      className="relative flex h-dvh max-h-dvh overflow-hidden bg-slate-50 dark:bg-slate-950"
    >
      <NavigationRecentsTracker />
      {sidebarOpen ? (
        <>
          {/* Mobile: dim main content; tap closes (desktop uses in-flow column). */}
          <button
            type="button"
            className="fixed inset-0 z-[45] bg-slate-900/40 md:hidden"
            aria-label="Close sidebar"
            data-testid="sidebar-mobile-backdrop"
            onClick={() => setSidebarOpen(false)}
          />
          <div
            className="fixed inset-y-0 left-0 z-50 flex flex-col shadow-xl md:relative md:z-auto md:shrink-0 md:shadow-none"
            style={{ width: sidebarPx }}
            data-testid="sidebar-shell"
          >
            <Sidebar />
          </div>
          <div className="hidden md:contents" data-testid="sidebar-resize-desktop-only">
            <SidebarResizeHandle
              onPointerDown={onResizerPointerDown}
              onReset={resetSidebarWidth}
              onClose={() => setSidebarOpen(false)}
              isDragging={isDragging}
            />
          </div>
        </>
      ) : (
        <>
          <button
            ref={reopenBtnRef}
            type="button"
            onClick={() => setSidebarOpen(true)}
            onMouseEnter={() => setReopenHover(true)}
            onMouseLeave={() => setReopenHover(false)}
            onFocus={() => setReopenHover(true)}
            onBlur={() => setReopenHover(false)}
            className="absolute left-2 top-[18px] z-[60] hidden rounded-lg p-2 text-slate-500 hover:bg-white hover:text-slate-800 md:inline-flex dark:hover:bg-slate-800 dark:hover:text-slate-200"
            aria-label="Open sidebar" title="Open sidebar"
            data-testid="sidebar-reopen-btn"
          >
            <PanelLeft className="h-5 w-5" />
          </button>
          {reopenHover && (
            <IconTooltip
              testId="sidebar-reopen-tooltip"
              anchorRef={reopenBtnRef}
              label="Open sidebar"
            />
          )}
        </>
      )}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <Header />
        <DemoBanner email={user?.email} />
        <main className="relative min-h-0 min-w-0 flex-1 overflow-auto" data-testid="main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
